import {
  Account,
  AccountDept,
  AccountExtraRef,
  AccountWebSetting,
  AddressDefaults,
  AddressWithType,
  Contact,
  ContactCard,
  CountriesOverride,
  Country,
  DocumentObj,
  Flag,
  Good,
  GoodsFlag,
  IntFavourite,
  Job,
  JobQuote,
  Stop,
  WebAccess,
} from './__generated__/graphql';
import { AddressType, MimeType } from '@/config/constants';
import {
  DocumentNode,
  OperationVariables,
  TypedDocumentNode,
} from '@apollo/client';

import { addTypeToAddress } from '@/utils/addressManipulation';
import config from '@/config';
import { getGoodsInfoKeyByType } from '@/utils/helpers';
import { getIntegraAppParts } from '@/api/lowLevel';
import { graphQlQuery, guestClient } from './client';
import { graphql } from './__generated__/gql';
import { useStore } from '@/store';

const baseQuery = async (
  queryName: string,
  query: DocumentNode | TypedDocumentNode<any, OperationVariables>,
  variables?: OperationVariables | undefined,
) => {
  return graphQlQuery(queryName, {
    query,
    fetchPolicy: 'no-cache',
    variables,
  });
};

const GET_APP_DATA = graphql(`
  #graphql
  query getAppData($codes: [String!]) {
    parameters(codes: $codes) {
      code
      setting
    }
  }
`);

export const getAppData = async (codes: string[]): Promise<any> => {
  return await baseQuery('getAppData', GET_APP_DATA, {
    codes,
  });
};

const GET_BASIC_CONTACT_DETAILS = graphql(`
  #graphql
  query getUserProfile($reference: Int!) {
    contact(reference: $reference) {
      reference
      accountRef
      contactName
      email
      mobile
      telephone
    }
  }
`);

export const getBasicContactDetails = async (
  reference = useStore().integraProfile.user.contactRef,
): Promise<Contact> => {
  return (
    await baseQuery('getBasicContactDetails', GET_BASIC_CONTACT_DETAILS, {
      reference,
    })
  )?.contact;
};

const GET_ACCOUNT_DETAILS = graphql(`
  #graphql
  query getAccountDetails($reference: String!) {
    account(reference: $reference) {
      reference
      name
    }
  }
`);

export const getAccountDetails = async (
  reference = useStore().integraProfile.user.accountRef,
): Promise<Account> => {
  return (
    await baseQuery('getAccountDetails', GET_ACCOUNT_DETAILS, {
      reference,
    })
  )?.account;
};

const GET_UNIVERSAL_ADDRESSES_PARTIAL = graphql(`
  #graphql
  query getUniversalAddressesPartial(
    $contactRef: Int
    $accountRef: String
    $partial: String
    $take: Int
    $types: [String!]
  ) {
    universalAddresses(
      contactRef: $contactRef
      accountRef: $accountRef
      partial: $partial
      take: $take
      types: $types
    ) {
      addressName
      address1
      address2
      address3
      address4
      postcode
      country
      countryCode
      lat
      long
      gridX
      gridY
      reference
      accountRef
      contactRef
      notes
      code
      searchAddress
      typeCode
    }
  }
`);

export const getUniversalAddressesPartial = async (
  contactRef = useStore().integraProfile.user.contactRef,
  accountRef = useStore().integraProfile.user.accountRef,
  partial: string,
  types: string[],
  take?: number,
): Promise<AddressWithType[]> => {
  types.sort((at: string) => {
    if (at === AddressType.CONTACT) {
      return -2;
    } else if (at === AddressType.ACCOUNT) {
      return -1;
    }
    return 0;
  });

  return (
    (
      await baseQuery(
        'getUniversalAddressesPartial',
        GET_UNIVERSAL_ADDRESSES_PARTIAL,
        {
          contactRef,
          accountRef,
          partial,
          types,
          take,
        },
      )
    ).universalAddresses ?? []
  );
};

const DEFAULT_ADDRESSES = graphql(`
  #graphql
  query defaultAddresses($contactRef: Int, $accountRef: String) {
    defaultAddresses(contactRef: $contactRef, accountRef: $accountRef) {
      account {
        reference
        addressName
        address1
        address2
        address3
        address4
        postcode
        country
        countryCode
        lat
        long
        gridX
        gridY
        reference
        code
        notes
        type {
          code
        }
      }
      contact {
        reference
        addressName
        address1
        address2
        address3
        address4
        postcode
        country
        countryCode
        lat
        long
        gridX
        gridY
        reference
        code
        notes
        type {
          code
        }
      }
    }
  }
`);

export const getDefaultAddresses = async (
  contactRef = useStore().integraProfile.user.contactRef,
  accountRef = useStore().integraProfile.user.accountRef,
): Promise<{
  contact: AddressWithType | null;
  account: AddressWithType | null;
}> => {
  const { contact, account } = (
    await baseQuery('defaultAddresses', DEFAULT_ADDRESSES, {
      reference: useStore().integraProfile.user.accountRef,
      contactRef,
      accountRef,
    })
  )?.defaultAddresses as AddressDefaults;

  return {
    contact: addTypeToAddress(contact),
    account: addTypeToAddress(account),
  };
};

const GET_ADDRESS_FROM_REF = graphql(`
  #graphql
  query Address($reference: Int!) {
    address(reference: $reference) {
      addressName
      address1
      address2
      address3
      address4
      postcode
      country
      countryCode
      lat
      long
      gridX
      gridY
      reference
      code
      notes
      type {
        code
      }
    }
  }
`);

export const getAddressFromRef = async (
  reference: number,
): Promise<AddressWithType | null> => {
  const address = (
    await baseQuery('getAddressFromRef', GET_ADDRESS_FROM_REF, {
      reference,
    })
  )?.address;

  return addTypeToAddress(address);
};

const GET_ADDRESS_BY_CODE = graphql(`
  #graphql
  query getAddressByCode(
    $addressCode: String!
    $contactRef: Int
    $accountRef: String
  ) {
    addressByCode(
      addressCode: $addressCode
      contactRef: $contactRef
      accountRef: $accountRef
    ) {
      addressName
      address1
      address2
      address3
      address4
      postcode
      country
      countryCode
      lat
      long
      gridX
      gridY
      reference
      code
      notes
      type {
        code
      }
    }
  }
`);

export const getAddressByCode = async (
  addressCode: string,
  contactRef = useStore().integraProfile.user.contactRef,
  accountRef = useStore().integraProfile.user.accountRef,
): Promise<AddressWithType | null> => {
  const address = (
    await baseQuery('getAddressByCode', GET_ADDRESS_BY_CODE, {
      addressCode,
      contactRef,
      accountRef,
    })
  )?.addressByCode;

  return addTypeToAddress(address);
};

const GET_ACCOUNT_EXTRA_REFS = graphql(`
  #graphql
  query getAccountExtraRefs($reference: String!) {
    account(reference: $reference) {
      reference
      extraRefs {
        allowDefaultValue
        allowedFormats
        compulsory
        defaultAccountDeptRef
        description
        disabled
        filterByGroups
        maxLength
        prompt
        reference
        sortOrder
        validated
        webSearchDdl
        reference
        calculatedDefault
      }
    }
  }
`);

export const getAccountExtraRefs = async (
  reference = useStore().integraProfile.user.accountRef,
): Promise<AccountExtraRef[]> => {
  return (
    (
      await baseQuery('getAccountExtraRefs', GET_ACCOUNT_EXTRA_REFS, {
        reference,
      })
    )?.account?.extraRefs ?? []
  );
};

const GET_CONTACT_EXTRA_REFS = graphql(`
  #graphql
  query getContactExtraRefs($reference: Int!) {
    contact(reference: $reference) {
      reference
      extraRefs {
        allowDefaultValue
        allowedFormats
        compulsory
        defaultAccountDeptRef
        description
        disabled
        filterByGroups
        maxLength
        prompt
        reference
        sortOrder
        validated
        webSearchDdl
        reference
        calculatedDefault(reference: $reference)
      }
    }
  }
`);

export const getContactExtraRefs = async (
  reference = useStore().integraProfile.user.contactRef,
): Promise<AccountExtraRef[]> => {
  return (
    (
      await baseQuery('getContactExtraRefs', GET_CONTACT_EXTRA_REFS, {
        reference,
      })
    )?.contact?.extraRefs ?? []
  );
};

const GET_EXTRA_REF_VALUES = graphql(`
  #graphql
  query getExtraRefValues(
    $reference: Int!
    $accountExtraRefRef: Int!
    $partial: String
  ) {
    contact(reference: $reference) {
      extraRefValues(
        accountExtraRefRef: $accountExtraRefRef
        partial: $partial
      ) {
        code
        description
        reference
      }
    }
  }
`);

export const getExtraRefValues = async (
  accountExtraRefRef: number,
  partial?: string,
): Promise<AccountDept[]> => {
  const store = useStore();
  return (
    (
      await baseQuery('getExtraRefValues', GET_EXTRA_REF_VALUES, {
        reference: store.integraProfile.user.contactRef,
        accountExtraRefRef,
        partial,
      })
    )?.contact?.extraRefValues ?? []
  );
};

const GET_JOB_DETAILS = graphql(`
  #graphql
  query getJobDetails(
    $reference: Int!
    $take: Int
    $typeCodes: [String!]
    $accountRef: String!
    $contactRef: Int!
    $appName: String!
    $platformName: String!
  ) {
    job(reference: $reference) {
      reference
      cancelReason
      holdReason
      dateCompleted
      tariff
      prebook
      pickupDt
      pickupDate
      dateEntered
      enteredBy
      estJourneyTime
      status
      normalisedEstPrice
      normalisedEstPriceVat
      normalisedEstPriceTotal
      currencyCode
      webInstructions
      contactRef
      contactName
      contactPhone
      contactMobile
      contactEmail
      eta
      otherRef
      accountDeptRef
      planGoods
      extraRefs {
        reference
        prompt
      }
      jobExtraRefs {
        reference
        accountExtraRefRef
        referenceValue
      }
      jobExtras {
        reference
        description
        quantity
        unit
      }
      stops {
        reference
        dropOrder
        addressName
        address1
        address2
        address3
        address4
        postcode
        country
        countryCode
        lat
        long
        gridX
        gridY
        notes
        requiredFromDt
        requiredToDt
        reqDtsManuallyEdited
        contactName
        contactTelephone
        contactMobile
        contactEmail
        asDirected
        asDirectedTime
        asDirectedTimeUnit
        podImages {
          reference
        }
      }
      goods {
        reference
        type
        description
        code
        code2
        info1
        info2
        info3
        unitWeight
        unitHeight
        unitWidth
        unitDepth
        unitValue
        unitCube
        unitCartons
        nonDoc
        dropStop
        pickupStop
        quantity
      }
      displayedJobFlags(
        accountRef: $accountRef
        contactRef: $contactRef
        appName: $appName
        platformName: $platformName
      ) {
        reference
        description
        sortOrder
      }
      tariffDetail {
        description
      }
      webTariff {
        label
        description
        appImgUrl
      }
      driver {
        firstname
        surname
        telMobile
        oLicenceNumber
        photo {
          picHeight
          picWidth
          dataUri
        }
      }
      vehicle {
        registration
        vehicleClass
        vehicleColour
        description
      }
      tracking(take: $take) {
        locationDt
        gridY
        gridX
        locationDir
        locationSpeed
        locationDesc
        timezoneOffsetMins
        locationDateTimeUtc
      }
      oagFlightJob {
        reference
        jobRef
        arrival
        stopRef
        flightDt
        fromToCity
        flightCode
        statusEta
        departAirportCode
        arriveAirportCode
        holdOff
      }
      vouchers {
        voucherRef
        voucherCode
      }
      customerVisibleDocuments {
        reference
        description
        source
        documentType {
          code
          description
        }
        documentObjs(typeCodes: $typeCodes) {
          reference
          typeDesc
          typeCode
          sortOrder
        }
      }
    }
  }
`);

export const getJobDetails = async (
  reference: number,
  documentObjectTypeCodes?: MimeType[],
  take?: number,
): Promise<Job> => {
  const { appName, platformName } = await getIntegraAppParts();

  return (
    await baseQuery('getJobDetails', GET_JOB_DETAILS, {
      reference,
      take,
      typeCodes: documentObjectTypeCodes,
      accountRef: useStore().integraProfile.user.accountRef,
      contactRef: useStore().integraProfile.user.contactRef,
      appName,
      platformName,
    })
  )?.job;
};

const GET_STOP_POD_IMAGES = graphql(`
  #graphql
  query getStopPodImages($reference: Int!) {
    job(reference: $reference) {
      stops {
        reference
        podImages {
          reference
          picName
          picWidth
          picHeight
          dataUri
        }
      }
    }
  }
`);

export const getStopPodImages = async (reference: number): Promise<Stop[]> => {
  return (
    (await baseQuery('getStopPodImages', GET_STOP_POD_IMAGES, { reference }))
      ?.job?.stops ?? []
  );
};

const GET_CONTACT_CARD_DETAILS = graphql(`
  #graphql
  query getContactCardDetails($reference: Int!) {
    contact(reference: $reference) {
      defaultContactCardRef
      contactCards {
        reference
        lastFourCharacters
        cardTypeCode
        validFrom
        validTo
        nameOnCard
      }
    }
  }
`);

export const getContactCardDetails = async (
  reference = useStore().integraProfile.user.contactRef,
): Promise<{
  contactCards: ContactCard[];
  defaultContactCardRef: number;
}> => {
  return (
    await baseQuery('getContactCards', GET_CONTACT_CARD_DETAILS, {
      reference,
    })
  )?.contact;
};

export const getContactAddresses = async (
  contactRef = useStore().integraProfile.user.contactRef,
  accountRef = useStore().integraProfile.user.accountRef,
): Promise<{
  addresses: AddressWithType[];
  defaultContactAddress: AddressWithType | null;
  defaultAccountAddress: AddressWithType | null;
}> => {
  const addresses = await getUniversalAddressesPartial(
    contactRef,
    accountRef,
    '',
    [AddressType.CONTACT, AddressType.ACCOUNT, AddressType.RECENT],
  );
  const { contact, account } = await getDefaultAddresses(
    contactRef,
    accountRef,
  );

  return {
    addresses,
    defaultContactAddress: contact,
    defaultAccountAddress: account,
  };
};

const GET_JOB_DETAILS_FOR_ACTION = graphql(`
  #graphql
  query getJobDetailsForAction($reference: Int!) {
    job(reference: $reference) {
      reference
      tariff
      pickupDt
      prebook
      waitReturn
      webInstructions
      webInstructionsMaxLength
      mop
      goodsQty
      contactRef
      contactName
      contactPhone
      contactMobile
      contactEmail
      invoiced
      holdReason
      dateCancelled
      arriveBy
      stops {
        reference
        dropOrder
        addressName
        address1
        address2
        address3
        address4
        postcode
        country
        countryCode
        lat
        long
        gridX
        gridY
        notes
        requiredFromDt
        requiredToDt
        reqDtsManuallyEdited
        contactName
        contactTelephone
        contactMobile
        contactEmail
        asDirected
        asDirectedTime
        asDirectedTimeUnit
      }
      goods {
        reference
        type
        description
        code
        code2
        info1
        info2
        info3
        unitWeight
        unitHeight
        unitWidth
        unitDepth
        unitValue
        unitCube
        unitCartons
        nonDoc
        dropStop
        pickupStop
        quantity
        goodsFlags {
          reference
          description
          disabled
        }
      }
      jobFlags
      accountDept
      otherRef
      jobExtraRefs {
        referenceValue
        accountExtraRefRef
      }
      oagFlightJob {
        stopRef
        flightCode
        fromToCity
        scheduledArrivalDt
        terminal
        arrival
        flightNo
        carrierCode
        scheduledDepartureDt
        scheduledArrivalDt
        departAirportCode
        arriveAirportCode
        holdOff
      }
      jobExtras {
        code
        quantity
      }
      vouchers {
        voucherRef
        voucherCode
      }
      jobCards {
        reference
        contactCard {
          lastFourCharacters
        }
      }
    }
  }
`);

export const getJobDetailsForAction = async (
  reference: number,
): Promise<Job> => {
  return (
    await baseQuery('getJobDetailsForAction', GET_JOB_DETAILS_FOR_ACTION, {
      reference,
    })
  )?.job;
};

const GET_JOB_QUOTE = graphql(`
  #graphql
  query getJobQuote($reference: Int!) {
    jobquote(reference: $reference) {
      reference
      tariff
      tariffRef
      jobRef
      pickupDt
      prebook
      waitReturn
      contactRef
      contactName
      webInstructions
      webInstructionsMaxLength
      mop
      jobFlags
      jobQuoteStops {
        reference
        dropOrder
        addressName
        address1
        address2
        address3
        address4
        postcode
        country
        countryCode
        lat
        long
        gridX
        gridY
        notes
        stopTime
      }
      accountDept
      otherRef
      jobQuoteExtraRefs {
        referenceValue
        accountextrarefRef
      }
    }
  }
`);

export const getJobQuote = async (reference: number): Promise<JobQuote> => {
  return (
    await baseQuery('getJobQuote', GET_JOB_QUOTE, {
      reference,
    })
  )?.jobquote;
};

const GET_FAVOURITE_JOURNEYS = graphql(`
  #graphql
  query FavouriteJourneys($reference: Int!, $take: Int) {
    contact(reference: $reference) {
      favouriteJourneys(take: $take) {
        reference
        planGoods
        tariff
        waitReturn
        webInstructions
        webInstructionsMaxLength
        mop
        goodsQty
        contactRef
        contactName
        contactPhone
        contactMobile
        contactEmail
        webTariff {
          label
          goodsType
        }
        stops {
          reference
          dropOrder
          addressName
          address1
          address2
          address3
          address4
          postcode
          country
          countryCode
          lat
          long
          gridX
          gridY
          notes
          requiredFromDt
          requiredToDt
          reqDtsManuallyEdited
          contactName
          contactTelephone
          contactMobile
          contactEmail
          asDirected
          asDirectedTime
          asDirectedTimeUnit
        }
        goods {
          reference
          type
          description
          code
          code2
          info1
          info2
          info3
          unitWeight
          unitHeight
          unitWidth
          unitDepth
          unitValue
          unitCube
          unitCartons
          nonDoc
          dropStop
          pickupStop
          goodsFlags {
            reference
            description
            disabled
          }
        }
        jobFlags
        accountDept
        otherRef
        jobExtraRefs {
          referenceValue
          accountExtraRefRef
        }
        oagFlightJob {
          stopRef
          flightCode
          fromToCity
          scheduledArrivalDt
          terminal
          arrival
          flightNo
          carrierCode
          scheduledDepartureDt
          scheduledArrivalDt
          departAirportCode
          arriveAirportCode
          holdOff
        }
      }
    }
  }
`);

export const getFavouriteJourneys = async (take?: number): Promise<Job[]> => {
  return (
    (
      await baseQuery('getFavouriteJourneys', GET_FAVOURITE_JOURNEYS, {
        reference: useStore().integraProfile.user.contactRef,
        take,
      })
    )?.contact?.favouriteJourneys ?? []
  );
};

const GET_CONTACT_FAVOURITE_JOURNEYS = graphql(`
  #graphql
  query getContactFavouriteJourneys($reference: Int!, $take: Int) {
    contact(reference: $reference) {
      intJobFavourites(take: $take) {
        reference
        jobRef
        description
        job {
          reference
          planGoods
          tariff
          waitReturn
          webInstructions
          webInstructionsMaxLength
          mop
          goodsQty
          contactRef
          contactName
          contactPhone
          contactMobile
          contactEmail
          webTariff {
            label
            goodsType
          }
          stops {
            reference
            dropOrder
            addressName
            address1
            address2
            address3
            address4
            postcode
            country
            countryCode
            lat
            long
            gridX
            gridY
            notes
            requiredFromDt
            requiredToDt
            reqDtsManuallyEdited
            contactName
            contactTelephone
            contactMobile
            contactEmail
            asDirected
            asDirectedTime
            asDirectedTimeUnit
          }
          goods {
            reference
            type
            description
            code
            code2
            info1
            info2
            info3
            unitWeight
            unitHeight
            unitWidth
            unitDepth
            unitValue
            unitCube
            unitCartons
            nonDoc
            dropStop
            pickupStop
            goodsFlags {
              reference
              description
              disabled
            }
          }
          jobFlags
          accountDept
          otherRef
          jobExtraRefs {
            referenceValue
            accountExtraRefRef
          }
          oagFlightJob {
            stopRef
            flightCode
            fromToCity
            scheduledArrivalDt
            terminal
            arrival
            flightNo
            carrierCode
            scheduledDepartureDt
            scheduledArrivalDt
            departAirportCode
            arriveAirportCode
            holdOff
          }
        }
      }
    }
  }
`);

export const getContactFavouriteJourneys = async (
  take?: number,
): Promise<IntFavourite[]> => {
  return (
    (
      await baseQuery(
        'getContactFavouriteJourneys',
        GET_CONTACT_FAVOURITE_JOURNEYS,
        {
          reference: useStore().integraProfile.user.contactRef,
          take,
        },
      )
    )?.contact?.intJobFavourites ?? []
  );
};

const GET_CONTACT_MASQUERADE_COUNTS = graphql(`
  #graphql
  query getContactMasqueradeCounts($reference: Int!) {
    contact(reference: $reference) {
      bookForContactsCount
      masqueradeAndMaintainContactsCount
    }
  }
`);

export const getContactMasqueradeCounts = async (
  reference = useStore().integraProfile.user.contactRef,
): Promise<{
  bookForContactsCount: number;
  masqueradeAndMaintainContactsCount: number;
}> => {
  return (
    await baseQuery(
      'getContactMasqueradeCounts',
      GET_CONTACT_MASQUERADE_COUNTS,
      {
        reference,
      },
    )
  )?.contact;
};

const GET_BOOK_FOR_CONTACTS = graphql(`
  #graphql
  query getBookForContacts($reference: Int!, $partial: String, $take: Int) {
    contact(reference: $reference) {
      bookForContacts(partial: $partial, take: $take) {
        reference
        accountRef
        contactName
        telephone
        mobile
        email
      }
    }
  }
`);

export const getBookForContacts = async (
  partial: string,
  reference = useStore().integraProfile.user.contactRef,
  take?: number,
): Promise<Contact[]> => {
  return (
    (
      await baseQuery('getBookForContacts', GET_BOOK_FOR_CONTACTS, {
        reference,
        partial,
        take,
      })
    )?.contact?.bookForContacts ?? []
  );
};

const GET_MASQUERADE_AND_MAINTAIN_CONTACTS = graphql(`
  #graphql
  query getMasqueradeAndMaintainContacts(
    $reference: Int!
    $partial: String
    $take: Int
  ) {
    contact(reference: $reference) {
      masqueradeAndMaintainContacts(partial: $partial, take: $take) {
        reference
        accountRef
        contactName
        telephone
        mobile
        email
      }
    }
  }
`);

export const getMasqueradeAndMaintainContacts = async (
  partial: string,
  reference = useStore().integraProfile.user.contactRef,
  take?: number,
): Promise<Contact[]> => {
  return (
    (
      await baseQuery(
        'getMasqueradeAndMaintainContacts',
        GET_MASQUERADE_AND_MAINTAIN_CONTACTS,
        {
          reference,
          partial,
          take,
        },
      )
    )?.contact?.masqueradeAndMaintainContacts ?? []
  );
};

const GET_CONTACT_WEB_ACCESS = graphql(`
  #graphql
  query getContactWebAccess($reference: Int!) {
    contact(reference: $reference) {
      webAccess {
        reference
        contactRef
        maintainContacts
        maintainChildContacts
        viewContacts
        editContacts
        disableContacts
        viewAddresses
        addAddresses
        editAddresses
        deleteAddresses
        viewCards
        addCards
        disableCards
        addJobs
        addJobsOthers
        editJobs
        editJobsOthers
        cancelJobs
        cancelJobsOthers
        canRequest
        maintainDepts
      }
    }
  }
`);

export const getContactWebAccess = async (
  reference = useStore().integraProfile.user.contactRef,
): Promise<WebAccess> => {
  return (
    await baseQuery('getContactWebAccess', GET_CONTACT_WEB_ACCESS, {
      reference,
    })
  )?.contact?.webAccess;
};

const GET_CONTACT_CALCULATED_DEFAULTS = graphql(`
  #graphql
  query getContactCalculatedDefaults($reference: Int!) {
    contact(reference: $reference) {
      calculatedDefaultTariff
      calculatedDefaultJobFlags
      calculatedDefaultMop
    }
  }
`);

export const getContactCalculatedDefaults = async (
  reference = useStore().integraProfile.user.contactRef,
): Promise<Contact> => {
  return (
    await baseQuery(
      'getContactCalculatedDefaults',
      GET_CONTACT_CALCULATED_DEFAULTS,
      {
        reference,
      },
    )
  )?.contact;
};

const GET_JOB_EMAILS = graphql(`
  #graphql
  query getJobEmails($reference: Int!) {
    job(reference: $reference) {
      reference
      contactEmail
      bookerEmail
      goods {
        info1
        info2
        info3
      }
    }
  }
`);

export const getJobEmails = async (reference: number): Promise<string[]> => {
  const profile = useStore().integraProfile;
  const job: Job = (
    await baseQuery('getJobEmails', GET_JOB_EMAILS, {
      reference,
    })
  )?.job;

  const emailKey = getGoodsInfoKeyByType('mail');
  return (
    [
      ...new Set([
        profile.user.email,
        ...(job.contactEmail ? [job.contactEmail] : []),
        ...(emailKey
          ? job.goods
              .map((good: Good) => good[emailKey])
              .filter((email): email is string => !!email)
          : []),
      ]),
    ].filter((email): email is string => !!email) ?? []
  );
};

const GET_JOB_ACCESS = graphql(`
  #graphql
  query getJobAccess($reference: Int!, $contactRef: Int!) {
    job(reference: $reference) {
      contactHasAccess(contactRef: $contactRef)
      isJobContact(contactRef: $contactRef)
    }
  }
`);

export const getJobAccess = async (
  reference: number,
  contactRef = useStore().integraProfile.user.contactRef,
): Promise<{ contactHasAccess: boolean; isJobContact: boolean }> => {
  return (
    await baseQuery('getJobAccess', GET_JOB_ACCESS, {
      reference,
      contactRef,
    })
  )?.job;
};

const GET_JOB_FLAGS = graphql(`
  #graphql
  query getJobFlags($flagIds: [Int!]) {
    flagsById(flagIds: $flagIds) {
      flagId
      note
      disabled
    }
  }
`);

export const getJobFlags = async (flagIds: number[]): Promise<Flag[]> => {
  return (
    (await baseQuery('getJobFlags', GET_JOB_FLAGS, { flagIds }))?.flagsById ??
    []
  );
};

const GET_GOODS_FLAGS = graphql(`
  #graphql
  query getGoodsFlags($flagRefs: [Int!]) {
    goodsFlagsByRef(flagRefs: $flagRefs) {
      reference
      disabled
      goodsType
    }
  }
`);

export const getGoodsFlags = async (
  flagRefs: number[],
): Promise<GoodsFlag[]> => {
  return (
    (await baseQuery('getGoodsFlags', GET_GOODS_FLAGS, { flagRefs }))
      ?.goodsFlagsByRef ?? []
  );
};

const GET_REWARD_REF_BY_CODE = graphql(`
  #graphql
  query getVoucherRefByCode($voucherCode: String!) {
    rewardRefByCode(voucherCode: $voucherCode)
  }
`);

export const getVoucherRefByCode = async (
  voucherCode: string,
): Promise<number | null> => {
  return (
    await baseQuery('getVoucherRefByCode', GET_REWARD_REF_BY_CODE, {
      voucherCode,
    })
  )?.rewardRefByCode;
};

const GET_ACCOUNT_WEB_SETTINGS_BY_ACCOUNT_REF = graphql(`
  #graphql
  query AccountWebSettingsByAccountRef($accountRef: String!) {
    account(reference: $accountRef) {
      accountWebSetting {
        priceIncTax
        defaultContactIsPassenger
        signupDefaultGroupRef
      }
    }
  }
`);

export const getAccountWebSettingsByAccountRef = async (
  accountRef = useStore().integraProfile.user.accountRef,
): Promise<AccountWebSetting> => {
  return (
    await baseQuery(
      'getAccountWebSettingsByAccountRef',
      GET_ACCOUNT_WEB_SETTINGS_BY_ACCOUNT_REF,
      {
        accountRef,
      },
    )
  )?.account?.accountWebSetting;
};

const GET_DOCUMENT_OBJECT = graphql(`
  #graphql
  query getDocumentObject($reference: Int!) {
    documentobj(reference: $reference) {
      reference
      description
      dataUri
      typeCode
    }
  }
`);

export const getDocumentObject = async (
  reference: number,
): Promise<DocumentObj> => {
  return (
    await baseQuery('getDocumentObject', GET_DOCUMENT_OBJECT, {
      reference,
    })
  )?.documentobj;
};

const GET_AVAILABLE_COUNTRIES_OVERRIDE = graphql(`
  #graphql
  query getAvailableCountriesOverride($accountRef: String!, $contactRef: Int) {
    availableCountriesOverride(
      accountRef: $accountRef
      contactRef: $contactRef
    ) {
      countryCode
      countryDescription
    }
  }
`);

export const getAvailableCountriesOverride = async (
  accountRef = useStore().integraProfile.user.accountRef,
  contactRef?: number,
): Promise<CountriesOverride[]> => {
  return (
    (
      await baseQuery(
        'getAvailableCountriesOverride',
        GET_AVAILABLE_COUNTRIES_OVERRIDE,
        {
          accountRef,
          contactRef,
        },
      )
    )?.availableCountriesOverride ?? []
  );
};

const GET_AVAILABLE_COUNTRIES = graphql(`
  #graphql
  query getAvailableCountries {
    availableCountries {
      iso2
      description
    }
  }
`);

export const getAvailableCountries = async (): Promise<Country[]> => {
  return (
    (await baseQuery('getAvailableCountries', GET_AVAILABLE_COUNTRIES))
      ?.availableCountries ?? []
  );
};

const GET_EARLIEST_DELAY = graphql(`
  #graphql
  query getEarliestDelay {
    earliestDelay
  }
`);

export const getEarliestDelay = async (): Promise<number> => {
  return (
    (await baseQuery('getEarliestDelay', GET_EARLIEST_DELAY))?.earliestDelay ??
    config.app.bookingForm.fallbackDefaultDelayMins
  );
};

const GET_QUICK_QUOTE_AVAILABLE = graphql(`
  #graphql
  query getQuickQuoteAvailable {
    quickQuoteAvailable
  }
`);

// Always asked as a guest, since the answer is wanted before anyone signs in.
// A failure throws rather than being logged here, leaving the caller to
// decide how quietly to treat it.
export const getQuickQuoteAvailable = async (): Promise<boolean> => {
  const { data } = await guestClient.query({
    query: GET_QUICK_QUOTE_AVAILABLE,
    fetchPolicy: 'no-cache',
  });

  return data.quickQuoteAvailable;
};
